import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { MDSComboboxMention, MDSComboboxVariant } from "@/mds/components/forms";
import {
  extractCollectionMentions,
  extractNoteMentions,
} from "@/mds/components/sessions/session-content/session-content.utils";
import { MDSComboboxFetchDataHandlerSessionInputFormContext } from "@/mds/components/sessions/session.types";
import { MDSZIndexes } from "@/mds/foundations/z-index";

const mentionId = (mention: MDSComboboxMention): string =>
  mention.kind === MemCommonMentionKind.Collection
    ? mention.collection_id
    : mention.note_id;

/**
 * A pane's composer chips, reachable from outside the combobox submit flow.
 * PTT sends bypass the form, so the voice path uses this to attach (and then
 * clear) the same context a typed message would carry.
 */
export interface ComposerMentionsHandle {
  buildContext: () =>
    MDSComboboxFetchDataHandlerSessionInputFormContext | undefined;
  clearMentions: () => void;
}

/**
 * What a mounted pane registers for PTT sends: its chips, and — for the
 * thread pane — where the message goes. Resolved as a thunk because the
 * SessionThread id only materializes on the first reply.
 */
export interface FloatingAgentComposerRegistration extends ComposerMentionsHandle {
  getReplyTo?: () => { eventId: string; sessionThreadId?: string };
}

export type FloatingAgentComposerRegistry = {
  main: FloatingAgentComposerRegistration | null;
  thread: FloatingAgentComposerRegistration | null;
};

/**
 * Context for a circle-mode PTT send (no composer mounted): the main
 * window's foregrounded notes go out as explicit mentions, exactly as the
 * open composer would send its chips.
 */
export const buildExternalMentionsContext = (
  externalMentions: MDSComboboxMention[]
): MDSComboboxFetchDataHandlerSessionInputFormContext | undefined => {
  if (externalMentions.length === 0) return undefined;

  return {
    mentions: [
      ...extractCollectionMentions(externalMentions),
      ...extractNoteMentions(externalMentions),
    ],
  };
};

/**
 * Per-composer @-mention chip state plus the combobox props the chat
 * composer uses (Brand variant, chips with add/remove/clear, instant fetch
 * so recents show on open without a loading flash). Each pane (main list,
 * thread) owns its own chips. `externalMentions` (the main window's
 * foregrounded notes) render as chips through the ordinary mentions path,
 * dismissable per pane, and ride along
 * on both typed and PTT sends as explicit mentions.
 */
export const useComposerMentions = (
  {
    externalMentions,
    pendingMentions,
    pendingMentionsRequestId,
    onMentionsAttached,
  }: {
    externalMentions?: MDSComboboxMention[];
    /**
     * Chips an in-app surface asked this composer to pre-attach, applied
     * whenever `pendingMentionsRequestId` changes. They land in the manual
     * chip list, so the user can remove them like any chip they added.
     */
    pendingMentions?: MDSComboboxMention[];
    pendingMentionsRequestId?: number;
    /** Called once a request is attached, so the source can clear it. */
    onMentionsAttached?: () => void;
  } = {}
) => {
  const [mentions, setMentions] = useState<MDSComboboxMention[]>([]);
  // Dismissed external chips stay dismissed by note id for this pane's
  // lifetime — refocusing the same note does NOT bring the chip back; only
  // closing and reopening the pane (fresh state) does.
  const [dismissedExternalIds, setDismissedExternalIds] = useState<
    ReadonlySet<string>
  >(new Set());

  // Apply a pre-attach request. Keyed on the request id alone: the same notes
  // requested twice must re-attach, and reading the array here would also fire
  // on an unrelated identity change.
  //
  // Starts unset rather than at the current id because the request is what
  // opens the panel: this composer usually mounts *after* the id was bumped,
  // so seeding it with that id would swallow the request that summoned it.
  const pendingMentionsRef = useRef(pendingMentions);
  pendingMentionsRef.current = pendingMentions;
  const onMentionsAttachedRef = useRef(onMentionsAttached);
  onMentionsAttachedRef.current = onMentionsAttached;
  const lastAppliedRequestId = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (pendingMentionsRequestId === undefined) return;
    if (pendingMentionsRequestId === lastAppliedRequestId.current) return;
    lastAppliedRequestId.current = pendingMentionsRequestId;

    const requested = pendingMentionsRef.current ?? [];
    if (requested.length === 0) return;

    // Dedupe within the request itself before merging: the same entity can
    // arrive twice in one selection (a note reachable through two panes).
    const requestedById = new Map(requested.map(mention => [mentionId(mention), mention]));
    const requestedIds = new Set(requestedById.keys());

    // A requested note may have been dismissed earlier as a foreground chip.
    // Explicitly re-attaching it has to override that dismissal, otherwise the
    // chip silently fails to appear.
    setDismissedExternalIds(current => {
      const next = new Set([...current].filter(id => !requestedIds.has(id)));
      return next.size === current.size ? current : next;
    });

    // Keep existing chips in place and append only genuinely new ids. Filtering
    // then re-appending the whole request reorders chips the user already had,
    // and any id present twice in `requested` would land twice.
    setMentions(current => {
      const seenIds = new Set(current.map(mentionId));
      const added = [...requestedById.values()].filter(
        mention => !seenIds.has(mentionId(mention)),
      );

      return added.length === 0 ? current : [...current, ...added];
    });

    onMentionsAttachedRef.current?.();
  }, [pendingMentionsRequestId]);

  const visibleExternalMentions = useMemo(
    () =>
      (externalMentions ?? []).filter(
        (mention) => !dismissedExternalIds.has(mentionId(mention)),
      ),
    [externalMentions, dismissedExternalIds],
  );

  // A note can be supplied by both sources at once — foregrounded in the app
  // and explicitly attached from a selection — so the merge dedupes by id and
  // keeps the external copy, which renders first.
  const allMentions = useMemo(() => {
    const merged: MDSComboboxMention[] = [...visibleExternalMentions];
    const seenIds = new Set(merged.map(mentionId));

    for (const mention of mentions) {
      if (seenIds.has(mentionId(mention))) continue;
      seenIds.add(mentionId(mention));
      merged.push(mention);
    }

    return merged;
  }, [visibleExternalMentions, mentions]);

  const onMentionAdd = useCallback((mention: MDSComboboxMention) => {
    setMentions((current) =>
      current.some((existing) => mentionId(existing) === mentionId(mention))
        ? current
        : [...current, mention],
    );
  }, []);

  const externalMentionsRef = useRef(externalMentions);
  externalMentionsRef.current = externalMentions;

  const onMentionRemove = useCallback((removedMentionId: string) => {
    // A note can be both foregrounded (external) and manually re-added via the
    // dropdown, so it may live in both sources at once. Clear both: dismiss the
    // external copy and drop the manual copy. Removing only one leaves the
    // other re-supplying the chip, making it un-removable until the pane reopens.
    const isExternal = (externalMentionsRef.current ?? []).some(
      (mention) => mentionId(mention) === removedMentionId,
    );
    if (isExternal) {
      setDismissedExternalIds((current) => {
        const next = new Set(current);
        next.add(removedMentionId);
        return next;
      });
    }
    setMentions((current) =>
      current.filter((mention) => mentionId(mention) !== removedMentionId),
    );
  }, []);

  // Post-send clear applies to the user's chips; the foreground-note chip is
  // ambient and stays while the note stays foregrounded.
  const onMentionsClear = useCallback(() => {
    setMentions([]);
  }, []);

  const comboboxProps = useMemo(
    () => ({
      placeholder: "Tell or ask Mem something",
      variant: MDSComboboxVariant.Brand,
      fetchIsInstant: true,
      mentions: allMentions,
      onMentionAdd,
      onMentionRemove,
      onMentionsClear,
      // The floating panel is a fixed z-index:550 overlay; the mention picker
      // portals to the body with z-index auto and would render behind it.
      popoverProps: {
        shouldFlip: true,
        style: { zIndex: MDSZIndexes.ReactAriaPopover },
      },
    }),
    [allMentions, onMentionAdd, onMentionRemove, onMentionsClear],
  );

  // Read through a ref so the handle stays identity-stable while chips change.
  const allMentionsRef = useRef(allMentions);
  allMentionsRef.current = allMentions;

  const buildContext = useCallback(():
    MDSComboboxFetchDataHandlerSessionInputFormContext | undefined => {
    const current = allMentionsRef.current;
    if (current.length === 0) return undefined;

    // Same conversion the combobox submit runs for typed messages.
    return {
      mentions: [
        ...extractCollectionMentions(current),
        ...extractNoteMentions(current),
      ],
    };
  }, []);

  return { comboboxProps, clearMentions: onMentionsClear, buildContext };
};
